import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@design-system';
import { DashboardPageHeader } from '@/components/DashboardPageHeader';
import {
  CARRIER_GUIDELINES,
  UnderwritingPromptVersionListItem,
  useUnderwritingPromptVersions,
} from '@/features/wizard-underwriting-guidelines';

export function WizardUnderwritingGuidelinesPage() {
  const { data: versions, isLoading } = useUnderwritingPromptVersions();

  const byKey = useMemo(() => {
    const map = new Map<
      string,
      {
        published?: UnderwritingPromptVersionListItem;
        draft?: UnderwritingPromptVersionListItem;
      }
    >();
    if (!versions) return map;
    for (const v of versions) {
      const entry = map.get(v.promptKey) ?? {};
      if (v.status === 'published') entry.published = v;
      else if (v.status === 'draft') entry.draft = v;
      map.set(v.promptKey, entry);
    }
    return map;
  }, [versions]);

  return (
    <main className="px-4 pb-12 pt-3 sm:px-6 lg:px-8">
      <DashboardPageHeader
        title="Underwriting Guidelines"
        description="Edit and version the carrier YAML guidelines that drive the AI underwriting prompts."
      />

      {isLoading ? (
        <div className="text-sm text-gray-500">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {CARRIER_GUIDELINES.map(carrier => {
            const entry = byKey.get(carrier.promptKey);
            const published = entry?.published;
            const draft = entry?.draft;
            return (
              <Link
                key={carrier.promptKey}
                to={`/wizard/underwriting-guidelines/${carrier.promptKey}`}
                className="block transition hover:opacity-90"
              >
                <Card>
                  <CardHeader>
                    <CardTitle>{carrier.carrierName}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    {published ? (
                      <div>
                        <span className="font-mono">v{published.versionNumber}</span> published
                        {' '}
                        {published.publishedAt
                          ? new Date(published.publishedAt).toLocaleDateString()
                          : ''}
                        {published.publishedByName && (
                          <span className="text-gray-500"> by {published.publishedByName}</span>
                        )}
                      </div>
                    ) : (
                      <div className="text-amber-700">Not yet published</div>
                    )}
                    {draft && (
                      <div className="text-amber-700">
                        Draft in progress (v{draft.versionNumber})
                      </div>
                    )}
                    <div className="text-xs text-gray-500 font-mono">{carrier.promptKey}</div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </main>
  );
}
